import { createContext, useContext, useEffect, useMemo } from 'react';
import { useInfiniteReportGroupAssets } from '@/hooks/api/reports/useInfiniteReportGroupAssets';
import { ReportGroupAsset } from '@/hooks/api/reports/useListReportGroupAssets';

interface ReportMasterDetailContextValue {
  hasMatchData: boolean;
  allAssets: ReportGroupAsset[];
  allAssetsTotal: number;
  isLoadingAllAssets: boolean;
  allAssetsError: Error | undefined;
}

const ReportMasterDetailContext = createContext<ReportMasterDetailContextValue | undefined>(undefined);

export const ReportMasterDetailProvider = ({
  reportId,
  groupId,
  children,
}: {
  reportId: string | undefined;
  groupId: string | undefined;
  children: React.ReactNode;
}) => {
  const {
    assets: allAssets,
    total: allAssetsTotal,
    isLoading: isLoadingFirstPage,
    error: allAssetsError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteReportGroupAssets({
    reportId,
    groupId,
    enabled: Boolean(reportId && groupId),
  });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Every group asset renders and the views sort across the whole group, so a
  // partially-paged group would show the wrong order, not just fewer rows.
  const isLoadingAllAssets = isLoadingFirstPage || hasNextPage;

  const hasMatchData = !isLoadingAllAssets && allAssets.some(a => a.matches && a.matches.length > 0);

  const value = useMemo(
    () => ({
      hasMatchData,
      allAssets,
      allAssetsTotal,
      isLoadingAllAssets,
      allAssetsError,
    }),
    [hasMatchData, allAssets, allAssetsTotal, isLoadingAllAssets, allAssetsError]
  );

  return <ReportMasterDetailContext.Provider value={value}>{children}</ReportMasterDetailContext.Provider>;
};

export const useReportMasterDetail = () => {
  const context = useContext(ReportMasterDetailContext);
  if (!context) {
    throw new Error('useReportMasterDetail must be used within ReportMasterDetailProvider');
  }
  return context;
};
